/**
 * Certification Types Hook
 * 
 * Manages certification types and categories that vendors can hold to demonstrate
 * compliance, qualifications, and industry standards. Certifications are key
 * factors in vendor selection and match scoring.
 * 
 * **Certification Hierarchy:**
 * - **Categories**: Top-level groupings (e.g., "Safety", "Quality", "Industry")
 * - **Types**: Specific certifications (e.g., "ISO 9001", "OSHA 30", "FDA Registered")
 * 
 * **Certification Structure:**
 * - Display order for consistent UI sorting
 * - Active/inactive status for lifecycle management
 * - Optional description for requirements details
 * - Category association for organization
 * 
 * **Verification Status:**
 * - Certifications can be uploaded by vendors
 * - Admin verification required before active
 * - Expiration tracking for time-sensitive certs
 * - Document storage for proof
 * 
 * **Key Features:**
 * - Fetch all certification categories
 * - Fetch all certification types (with category data)
 * - Filter by category
 * - Active-only or all certifications
 * - Automatic cache management via React Query
 * 
 * **Use Cases:**
 * - Vendor profile certification selection
 * - RFQ certification requirements
 * - Admin certification management
 * - Compliance verification
 * - Match scoring calculations
 * 
 * **Common Certification Types:**
 * - Quality: ISO 9001, Six Sigma, AS9100
 * - Safety: OSHA, ISO 45001
 * - Environmental: ISO 14001, EPA
 * - Industry-specific: FDA, FAA, CE Mark
 * 
 * @returns {Object} Certification types and categories
 * @returns {CertificationCategory[]} categories - Array of certification categories
 * @returns {boolean} categoriesLoading - Categories loading state
 * @returns {CertificationType[]} types - Array of certification types
 * @returns {boolean} typesLoading - Types loading state
 * 
 * @example
 * // Display certification options
 * const { categories, types, categoriesLoading, typesLoading } = useCertificationTypes();
 * 
 * if (categoriesLoading || typesLoading) return <Spinner />;
 * 
 * categories.map(category => (
 *   <CertificationSection key={category.id} title={category.name}>
 *     {types
 *       .filter(type => type.category_id === category.id)
 *       .map(type => (
 *         <CertificationOption
 *           key={type.id}
 *           name={type.name}
 *           description={type.description}
 *         />
 *       ))}
 *   </CertificationSection>
 * ));
 * 
 * @example
 * // Filter by category
 * const { types } = useCertificationTypes();
 * 
 * const qualityCerts = types.filter(
 *   type => type.category?.name === 'Quality Management'
 * );
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CertificationCategory {
  id: string;
  name: string;
  display_order: number;
  is_active: boolean;
}

export interface CertificationType {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  category?: CertificationCategory;
}

export const useCertificationTypes = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all certification categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['certification-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certification_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return data as CertificationCategory[];
    },
  });

  // Fetch all certification types with category data
  const { data: types = [], isLoading: typesLoading } = useQuery({
    queryKey: ['certification-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certification_types')
        .select(`
          *,
          category:certification_categories(*)
        `)
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return data as CertificationType[];
    },
  });

  // Get types for a specific category
  const getTypesByCategory = (categoryId: string) => {
    return types.filter(type => type.category_id === categoryId);
  };

  return {
    categories,
    types,
    getTypesByCategory,
    isLoading: categoriesLoading || typesLoading,
  };
};
